import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { OrderStatus } from "./types";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
} 

/**
 * Format a number as currency (INR)
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Format a date string
 */
export function formatDate(date: string | Date): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(dateObj);
}

export function formatDateShort(date: string | Date): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-IN', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(dateObj);
}

export function formatTime(date: string | Date): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(dateObj);
}

export function getOrderStatusBadgeVariant(status: OrderStatus): string {
  switch (status) {
    case 'pending':
      return 'pending';
    case 'confirmed':
      return 'confirmed';
    case 'processing':
      return 'processing';
    case 'ready_for_pickup':
    case 'picked_up':
    case 'in_transit':
      return 'transit';
    case 'issue_reported':
      return 'issue';
    case 'delivered':
    case 'completed':
      return 'completed';
    case 'cancelled':
    case 'rejected':
    case 'refunded':
      return 'cancelled';
    default:
      return 'default';
  }
}

export function formatOrderStatus(status: OrderStatus): string {
  return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

export function getStatusColor(status: OrderStatus): string {
  switch (status) {
    case 'pending':
      return 'text-yellow-600';
    case 'confirmed':
    case 'processing':
      return 'text-blue-600';
    case 'ready_for_pickup':
    case 'picked_up':
    case 'in_transit':
      return 'text-purple-600';
    case 'issue_reported':
      return 'text-orange-600';
    case 'delivered':
    case 'completed':
      return 'text-green-600';
    case 'cancelled':
    case 'rejected':
    case 'refunded':
      return 'text-red-600';
    default:
      return 'text-gray-600';
  }
}

export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
} 